import React, { useEffect, useState } from "react";
import CapabilityMatrix from "./CapabilityMatrix";
import { Card, Button } from "@material-ui/core";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  ArcElement,
  Tooltip,
  Legend,
  Title
} from "chart.js";
import { MatrixController, MatrixElement } from "chartjs-chart-matrix";
import {
  Chart as ReactChart, // React wrapper
  Bar,
  Pie,
  Line,
  Doughnut,
  Radar,
  PolarArea
} from "react-chartjs-2";
import { useHistory } from "react-router-dom";


// Register everything Chart.js needs
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  ArcElement,
  MatrixController,
  MatrixElement,
  Tooltip,
  Legend,
  Title
);


// Animated Counter Component
const AnimatedCounter = ({ value, duration = 1000 }) => {
  const [count, setCount] = useState(0);


  useEffect(() => {
    let start = 0;
    const end = parseInt(value, 10);
    if (isNaN(end) || end <= 0) {
      setCount(0);
      return;
    }

    const stepTime = Math.max(Math.floor(duration / (end || 1)), 20);
    const timer = setInterval(() => {
      start += 1;
      setCount(start);
      if (start >= end) clearInterval(timer);
    }, stepTime);

    return () => clearInterval(timer);
  }, [value, duration]);

  return <span>{count}</span>;
};

const ModuleAnalyticsBox = ({
  title,
  value,
  unit,
  chartType,
  chartData,
  actions = [],
  stats = [],
  hideLegend = false,
}) => {
  // check if chartData has numbers
  const hasData =
    chartType === "matrix"
      ? chartData && chartData.raw && chartData.raw.length > 0
      : chartData &&
      chartData.datasets &&
      chartData.datasets.length > 0 &&
      chartData.datasets[0].data &&
      chartData.datasets[0].data.length > 0;
  let history = useHistory();

  const renderChart = () => {
    if (!hasData) {
      return (
        <div
          style={{
            height: chartType === "matrix" ? "auto" : "180px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            background: "linear-gradient(135deg,#f9fafc,#f3f4f7)",
            borderRadius: "12px",
            border: "1px dashed #ccc",
            color: "#888",
            padding: "16px",
          }}
        >
          <div style={{ fontSize: "2.5rem", marginBottom: "8px" }}>📊</div>
          <p style={{ margin: 0, fontSize: "1rem", fontWeight: 500 }}>
            No data available
          </p>
          <p style={{ margin: "4px 0", fontSize: "0.85rem", color: "#666" }}>
            Connect a source or upload data to see insights.
          </p>
          {actions.length > 0 && (
            <Button
              variant="contained"
              color="primary"
              size="small"
              style={{ marginTop: "8px", borderRadius: "20px" }}
              onClick={actions[0].onClick}
            >
              {actions[0].label}
            </Button>
          )}
        </div>
      );
    }


    switch (chartType) {
      case "bar":
        return <Bar data={chartData} options={{
          maintainAspectRatio: false, plugins: {
            legend: { display: !hideLegend },
          },
        }} />;
      case "line":
        return <Line data={chartData} options={{ maintainAspectRatio: false }} />;
      case "pie":
        return <Pie data={chartData} options={{ maintainAspectRatio: false }} />;
      case "doughnut":
        return <Doughnut data={chartData} options={{ maintainAspectRatio: false }} />;
      case "radar":
        return <Radar data={chartData} options={{ maintainAspectRatio: false }} />;
      case "polar":
        return <PolarArea data={chartData} options={{ maintainAspectRatio: false }} />;
      case "matrix":
        return <CapabilityMatrix data={chartData.raw} actions={[
          {
            label: "Workbench",
            onClick: () => history.push("/capability"),
          },
          {
            label: "Capability Mapping",
            onClick: () => history.push("/capability-mapping"),
          },
        ]} />;

      default:
        return <Bar data={chartData} options={{ maintainAspectRatio: false }} />;
    }
  };

  return (
    <Card
      style={{
        borderRadius: "20px",
        padding: "20px",
        background: "#fff",
        boxShadow: "0 8px 24px rgba(0,0,0,0.1)",
      }}
    >
      <h5 style={{ margin: 0, fontWeight: 600, color: "#333" }}>{title}</h5>

      {stats.length > 0 && (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginTop: "10px",
            padding: "8px 12px",
            background: "#f9fafc",
            borderRadius: "12px",
          }}
        >
          {stats.map((s, i) => (
            <div key={i} style={{ textAlign: "center", flex: 1 }}>
              <div style={{ fontSize: "1.4rem", fontWeight: "700", color: s.color }}>
                {typeof s.value === "number" ? (
                  <>
                    <AnimatedCounter value={s.value} />%
                  </>
                ) : (
                  s.value
                )}
              </div>
              <div style={{ fontSize: "0.8rem", color: "#555" }}>{s.label}</div>
            </div>
          ))}
        </div>
      )}

      {hasData && stats.length === 0 && (
        <div
          style={{
            fontSize: "2rem",
            fontWeight: 700,
            color: "#1976d2",
            margin: "10px 0",
          }}
        >
          <AnimatedCounter value={value} />{" "}
          <span style={{ fontSize: "1rem", color: "#555" }}>{unit}</span>
        </div>
      )}

      <div
        style={{
          height: chartType === "matrix" ? "auto" : "180px",
          marginBottom: "10px",
          overflowX: chartType === "matrix" ? "auto" : "hidden"
        }}
      >
        {renderChart()}
      </div>


      {hasData && actions.length > 0 && (
        <div style={{ display: "flex", justifyContent: "flex-start", marginTop: "10px" }}>
          {actions.map((action, i) => (
            <Button
              key={i}
              variant="outlined"
              color="primary"
              size="small"
              style={{ borderRadius: "20px", textTransform: "none", marginRight: "8px" }}
              onClick={action.onClick}
            >
              {action.label}
            </Button>
          ))}
        </div>
      )}
    </Card>
  );
};

export default ModuleAnalyticsBox;
